import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Save, User, BookOpen, Layout, Link2, CheckCircle } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTaxonomy } from '../../contexts/TaxonomyContext'
import api from '../../services/api'

type NavPreference = 'search' | 'hierarchy' | 'graph'

interface SettingsForm {
  preferredTaxonomy: string
  preferredNav: NavPreference
}

const NAV_OPTIONS: { value: NavPreference; label: string; description: string }[] = [
  { value: 'search', label: 'Search First', description: 'Start with keyword and semantic search' },
  { value: 'hierarchy', label: 'Hierarchy First', description: 'Browse taxonomy hierarchies to find resources' },
  { value: 'graph', label: 'Graph First', description: 'Explore resource connections visually' },
]

export default function Settings() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { taxonomies } = useTaxonomy()
  const [saved, setSaved] = useState(false)
  const [form, setForm] = useState<SettingsForm>({
    preferredTaxonomy: user?.preferredTaxonomy || 'anzsrc-2020',
    preferredNav: user?.preferredNav || 'search',
  })

  useEffect(() => {
    if (user) {
      setForm({
        preferredTaxonomy: user.preferredTaxonomy || 'anzsrc-2020',
        preferredNav: user.preferredNav || 'search',
      })
    }
  }, [user])

  const saveMutation = useMutation({
    mutationFn: async (settings: SettingsForm) => {
      const { data } = await api.put('/academic/settings', {
        preferred_taxonomy: settings.preferredTaxonomy,
        preferred_nav: settings.preferredNav,
      })
      return data
    },
    onSuccess: () => {
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const handleSave = () => {
    saveMutation.mutate(form)
  }

  const roleBadgeColors: Record<string, string> = {
    researcher: 'bg-blue-50 text-blue-700',
    'uni-admin': 'bg-purple-50 text-purple-700',
    'institution-admin': 'bg-amber-50 text-amber-700',
    'system-admin': 'bg-red-50 text-red-700',
  }

  const roleLabels: Record<string, string> = {
    researcher: 'Researcher',
    'uni-admin': 'University Admin',
    'institution-admin': 'Institution Admin',
    'system-admin': 'System Admin',
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your profile and preferences</p>
      </div>

      <div className="space-y-6">
        {/* Profile Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-acad-600" />
            <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
              <p className="text-sm text-gray-900 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                {user?.email || 'Not available'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                  roleBadgeColors[user?.role || 'researcher'] || 'bg-gray-50 text-gray-700'
                }`}
              >
                {roleLabels[user?.role || 'researcher'] || user?.role || 'Researcher'}
              </span>
            </div>
            {user?.institutionId && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Institution</label>
                <p className="text-sm text-gray-900">{user.institutionId}</p>
              </div>
            )}
            {user?.universityId && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">University</label>
                <p className="text-sm text-gray-900">{user.universityId}</p>
              </div>
            )}
          </div>
        </div>

        {/* Preferred Taxonomy */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-5 w-5 text-acad-600" />
            <h2 className="text-lg font-semibold text-gray-900">Preferred Taxonomy</h2>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Select the default taxonomy used for browsing and subject classification.
          </p>
          <select
            value={form.preferredTaxonomy}
            onChange={(e) => setForm((prev) => ({ ...prev, preferredTaxonomy: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-acad-500 focus:ring-1 focus:ring-acad-500 bg-white"
          >
            {taxonomies.map((tax) => (
              <option key={tax.id} value={tax.id}>
                {tax.name}
              </option>
            ))}
          </select>
        </div>

        {/* Preferred Navigation */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Layout className="h-5 w-5 text-acad-600" />
            <h2 className="text-lg font-semibold text-gray-900">Preferred Navigation</h2>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Choose your preferred way to discover resources when you open the portal.
          </p>
          <div className="space-y-2">
            {NAV_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                  form.preferredNav === option.value
                    ? 'border-acad-300 bg-acad-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="preferredNav"
                  value={option.value}
                  checked={form.preferredNav === option.value}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      preferredNav: e.target.value as NavPreference,
                    }))
                  }
                  className="mt-0.5 h-4 w-4 text-acad-500 focus:ring-acad-500 border-gray-300"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{option.label}</p>
                  <p className="text-xs text-gray-500">{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Mendeley Connection */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-acad-600" />
              <h2 className="text-lg font-semibold text-gray-900">Mendeley</h2>
            </div>
            <button
              onClick={() => navigate('/mendeley')}
              className="text-sm font-medium text-acad-600 hover:text-acad-700 transition-colors"
            >
              Manage Connection &rarr;
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Connect your Mendeley account to import and export research references.
          </p>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end gap-3">
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-green-600">
              <CheckCircle className="h-4 w-4" />
              Settings saved
            </span>
          )}
          {saveMutation.isError && (
            <span className="text-sm text-red-600">Failed to save. Please try again.</span>
          )}
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-acad-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  )
}
